import { useCallback, useEffect, useId, useState } from "react";
import {
  approveNumberRequest,
  denyNumberRequest,
  getAdminLeasedNumbers,
  getAdminNumberRequests,
  searchAdminAvailableNumbers,
  setOrganizationMessagingQuota,
} from "../../services/messaging";
import { ApiError } from "../../services/api";
import type {
  AdminLeasedNumbers,
  AvailableNumber,
  MessagingNumberRequest,
} from "../../types/messaging";
import { AdminPage, AdminPageHeader, AdminPanel } from "./AdminPage";
import "./MessagingNumbersSection.css";

const formatE164 = (e164: string): string => {
  const digits = e164.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return e164;
};

const formatCost = (cost: number | null): string =>
  cost === null ? "unknown" : `$${cost.toFixed(2)}`;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/**
 * Platform admin view of number leasing: the cross-organization request queue, and what
 * every leased number costs RosterPilot per month.
 *
 * The cost total is deliberately presented as a floor. Twilio's own number list is the
 * source of truth for the invoice, so a number missing a recorded price is counted rather
 * than guessed at, and anything on the invoice that is absent here is a number being paid
 * for and not tracked.
 */
export const MessagingNumbersSection = () => {
  const [requests, setRequests] = useState<MessagingNumberRequest[]>([]);
  const [leased, setLeased] = useState<AdminLeasedNumbers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [busyRequestId, setBusyRequestId] = useState<string | null>(null);
  const [denyingId, setDenyingId] = useState<string | null>(null);
  const [denyNote, setDenyNote] = useState("");

  // Search is scoped to whichever request the admin is fulfilling, so results cannot be
  // applied to the wrong organization by accident.
  const [searchForId, setSearchForId] = useState<string | null>(null);
  const [searchAreaCode, setSearchAreaCode] = useState("");
  const [searchResults, setSearchResults] = useState<AvailableNumber[] | null>(null);
  const [searching, setSearching] = useState(false);

  const [quotaOrgId, setQuotaOrgId] = useState<string | null>(null);
  const [quotaValue, setQuotaValue] = useState("");

  const searchGroupLabelId = useId();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedRequests, loadedNumbers] = await Promise.all([
        getAdminNumberRequests(),
        getAdminLeasedNumbers(),
      ]);
      setRequests(loadedRequests);
      setLeased(loadedNumbers);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not load messaging number data."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = requests.filter((r) => r.status === "Pending");
  const decided = requests.filter((r) => r.status !== "Pending");

  const handleSearch = async () => {
    setError(null);
    setSearching(true);
    try {
      const results = await searchAdminAvailableNumbers({
        areaCode: searchAreaCode.trim() || undefined,
      });
      setSearchResults(results);
    } catch (err) {
      setSearchResults(null);
      setError(
        err instanceof ApiError && err.statusCode === 503
          ? "Number leasing is not configured in this environment."
          : err instanceof Error
            ? err.message
            : "Could not search for numbers."
      );
    } finally {
      setSearching(false);
    }
  };

  const handleApprove = async (
    request: MessagingNumberRequest,
    phoneNumber?: string
  ) => {
    setError(null);
    setNotice(null);
    setBusyRequestId(request.id);
    try {
      const updated = await approveNumberRequest(request.id, {
        phoneNumber: phoneNumber ?? null,
      });
      setNotice(
        `${request.organizationName ?? "That organization"} now has ${
          updated.fulfilledPhoneNumber
            ? formatE164(updated.fulfilledPhoneNumber)
            : "a new number"
        }.`
      );
      setSearchForId(null);
      setSearchResults(null);
      await load();
    } catch (err) {
      const code =
        err instanceof ApiError &&
        err.response !== null &&
        typeof err.response === "object"
          ? (err.response as { code?: string }).code
          : undefined;

      if (code === "number_unavailable") {
        // The request stays pending on the server, so the admin can just pick another.
        setError(
          "That number was taken before we could buy it. Search again and pick another."
        );
        setSearchForId(request.id);
        setSearchResults(
          (current) =>
            current?.filter((n) => n.phoneNumber !== phoneNumber) ?? null
        );
      } else {
        setError(
          err instanceof Error ? err.message : "Could not set up that number."
        );
      }
    } finally {
      setBusyRequestId(null);
    }
  };

  const handleDeny = async (request: MessagingNumberRequest) => {
    setError(null);
    setNotice(null);
    setBusyRequestId(request.id);
    try {
      await denyNumberRequest(request.id, denyNote.trim());
      setDenyingId(null);
      setDenyNote("");
      setNotice("Request denied. The organization can see your note.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not deny that request.");
    } finally {
      setBusyRequestId(null);
    }
  };

  const handleSetQuota = async (organizationId: string) => {
    setError(null);
    setNotice(null);
    const trimmed = quotaValue.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);

    if (parsed !== null && (!Number.isInteger(parsed) || parsed < 0)) {
      setError("A quota has to be a whole number, or blank to use the default.");
      return;
    }

    try {
      const quota = await setOrganizationMessagingQuota(organizationId, parsed);
      setQuotaOrgId(null);
      setQuotaValue("");
      setNotice(
        `Quota set to ${quota.limit}. They are using ${quota.used}.`
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not set that quota.");
    }
  };

  return (
    <AdminPage className="messaging-numbers-section">
      <AdminPageHeader
        title="Messaging numbers"
        subtitle={
          leased && (
            <span className="messaging-numbers-section__total">
              {leased.numbers.length} leased, at least{" "}
              {formatCost(leased.knownMonthlyCostUsd)} a month
              {leased.numbersMissingCost > 0 &&
                ` (${leased.numbersMissingCost} with no recorded price)`}
            </span>
          )
        }
      />

      <div
        role="status"
        aria-live="polite"
        className="messaging-numbers-section__announcer"
      >
        {error && <div className="admin-error">{error}</div>}
        {notice && <div className="messaging-numbers-section__notice">{notice}</div>}
      </div>

      <AdminPanel
        title={`Waiting on a decision (${pending.length})`}
        description={
          <>
            Approving buys a real number on RosterPilot's Twilio account and hands it to that
            organization over its quota. If the same organization keeps asking, raise its
            quota instead of approving one at a time.
          </>
        }
      >
        {loading ? (
          <p className="admin-state">Loading...</p>
        ) : pending.length === 0 ? (
          <p className="admin-state">
            Nothing waiting on you.
          </p>
        ) : (
          <ul className="messaging-numbers-section__requests">
            {pending.map((request) => (
              <li key={request.id} className="messaging-numbers-section__request">
                <div className="messaging-numbers-section__request-head">
                  <span className="messaging-numbers-section__org">
                    {request.organizationName ?? "Unknown organization"}
                  </span>
                  <span className="messaging-numbers-section__asked">
                    {request.requestedE164
                      ? formatE164(request.requestedE164)
                      : request.areaCode
                        ? `Area code ${request.areaCode}`
                        : "No preference"}
                  </span>
                  <span className="messaging-numbers-section__date">
                    {formatDate(request.createdAt)}
                  </span>
                </div>

                {request.reason && (
                  <p className="messaging-numbers-section__reason">{request.reason}</p>
                )}

                <div className="messaging-numbers-section__actions">
                  {request.requestedE164 && (
                    <button
                      type="button"
                      onClick={() => void handleApprove(request)}
                      disabled={busyRequestId !== null}
                      aria-label={`Approve ${formatE164(request.requestedE164)} for ${request.organizationName ?? "this organization"}`}
                    >
                      {busyRequestId === request.id
                        ? "Buying..."
                        : `Approve ${formatE164(request.requestedE164)}`}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchForId(request.id);
                      setSearchAreaCode(request.areaCode ?? "");
                      setSearchResults(null);
                    }}
                    aria-label={`Pick a different number for ${request.organizationName ?? "this organization"}`}
                  >
                    Pick a number
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDenyingId(request.id);
                      setDenyNote("");
                    }}
                    aria-label={`Deny the request from ${request.organizationName ?? "this organization"}`}
                  >
                    Deny
                  </button>
                  {request.organizationId && (
                    <button
                      type="button"
                      onClick={() => {
                        setQuotaOrgId(request.organizationId);
                        setQuotaValue("");
                      }}
                      aria-label={`Change the number quota for ${request.organizationName ?? "this organization"}`}
                    >
                      Change quota
                    </button>
                  )}
                </div>

                {denyingId === request.id && (
                  <div className="messaging-numbers-section__inline-form">
                    <label htmlFor={`deny-note-${request.id}`}>
                      Why not? They will see this.
                    </label>
                    <input
                      id={`deny-note-${request.id}`}
                      type="text"
                      value={denyNote}
                      onChange={(e) => setDenyNote(e.target.value)}
                      placeholder="Use the two numbers you already have first"
                    />
                    <button
                      type="button"
                      onClick={() => void handleDeny(request)}
                      disabled={!denyNote.trim() || busyRequestId !== null}
                    >
                      Send denial
                    </button>
                    <button type="button" onClick={() => setDenyingId(null)}>
                      Cancel
                    </button>
                  </div>
                )}

                {quotaOrgId === request.organizationId && quotaOrgId !== null && (
                  <div className="messaging-numbers-section__inline-form">
                    <label htmlFor={`quota-${request.id}`}>
                      New quota (blank for the default)
                    </label>
                    <input
                      id={`quota-${request.id}`}
                      type="text"
                      inputMode="numeric"
                      value={quotaValue}
                      onChange={(e) => setQuotaValue(e.target.value)}
                      placeholder="10"
                    />
                    <button
                      type="button"
                      onClick={() => void handleSetQuota(quotaOrgId)}
                    >
                      Save quota
                    </button>
                    <button type="button" onClick={() => setQuotaOrgId(null)}>
                      Cancel
                    </button>
                  </div>
                )}

                {searchForId === request.id && (
                  <div className="messaging-numbers-section__search">
                    <span
                      id={searchGroupLabelId}
                      className="messaging-numbers-section__group-label"
                    >
                      Find a number for {request.organizationName ?? "this organization"}
                    </span>
                    <div
                      className="messaging-numbers-section__inline-form"
                      role="group"
                      aria-labelledby={searchGroupLabelId}
                    >
                      <label htmlFor={`area-${request.id}`}>Area code</label>
                      <input
                        id={`area-${request.id}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={3}
                        value={searchAreaCode}
                        onChange={(e) =>
                          setSearchAreaCode(e.target.value.replace(/\D/g, ""))
                        }
                        placeholder="513"
                      />
                      <button
                        type="button"
                        onClick={handleSearch}
                        disabled={searching}
                      >
                        {searching ? "Searching..." : "Search"}
                      </button>
                    </div>

                    <div
                      role="status"
                      aria-live="polite"
                      className="messaging-numbers-section__announcer"
                    >
                      {searchResults !== null &&
                        (searchResults.length === 0
                          ? "No numbers matched."
                          : `${searchResults.length} available.`)}
                    </div>

                    {searchResults !== null && searchResults.length > 0 && (
                      <ul className="messaging-numbers-section__results">
                        {searchResults.map((candidate) => (
                          <li key={candidate.phoneNumber}>
                            <span className="messaging-numbers-section__number">
                              {formatE164(candidate.phoneNumber)}
                            </span>
                            <span className="messaging-numbers-section__date">
                              {[candidate.locality, candidate.region]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                void handleApprove(request, candidate.phoneNumber)
                              }
                              disabled={busyRequestId !== null}
                              aria-label={`Approve ${formatE164(candidate.phoneNumber)} for ${request.organizationName ?? "this organization"}`}
                            >
                              Approve this one
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </AdminPanel>

      {decided.length > 0 && (
        <AdminPanel title={`Already decided (${decided.length})`}>
          <ul className="messaging-numbers-section__requests">
            {decided.map((request) => (
              <li key={request.id} className="messaging-numbers-section__request">
                <div className="messaging-numbers-section__request-head">
                  <span className="messaging-numbers-section__org">
                    {request.organizationName ?? "Unknown organization"}
                  </span>
                  <span className="messaging-numbers-section__asked">
                    {request.status}
                    {request.fulfilledPhoneNumber &&
                      `: ${formatE164(request.fulfilledPhoneNumber)}`}
                  </span>
                  <span className="messaging-numbers-section__date">
                    {request.decidedAt
                      ? formatDate(request.decidedAt)
                      : formatDate(request.createdAt)}
                  </span>
                </div>
                {request.decisionNote && (
                  <p className="messaging-numbers-section__reason">
                    {request.decisionNote}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </AdminPanel>
      )}

      <AdminPanel
        title={`Leased numbers (${leased?.numbers.length ?? 0})`}
        description={
          <>
            Every number RosterPilot bought on an organization's behalf. Compare this against
            Twilio's own number list now and then: anything on the invoice that is not here is
            a number being paid for and not tracked.
          </>
        }
      >
        {leased && leased.numbers.length > 0 ? (
          <div className="messaging-numbers-section__table-scroll">
            <table className="messaging-numbers-section__table">
              <caption className="messaging-numbers-section__caption">
                Leased numbers by organization, with monthly cost
              </caption>
              <thead>
                <tr>
                  <th scope="col">Number</th>
                  <th scope="col">Organization</th>
                  <th scope="col">Label</th>
                  <th scope="col">Cost</th>
                  <th scope="col">Since</th>
                </tr>
              </thead>
              <tbody>
                {leased.numbers.map((number) => (
                  <tr key={number.id}>
                    <td className="messaging-numbers-section__number">
                      {formatE164(number.phoneNumber)}
                    </td>
                    <td>{number.organizationName}</td>
                    <td>{number.friendlyName ?? "none"}</td>
                    <td>{formatCost(number.monthlyCostUsd)}</td>
                    <td>{formatDate(number.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="admin-state">
            No numbers have been leased yet.
          </p>
        )}
      </AdminPanel>
    </AdminPage>
  );
};
